"use client"

import type React from "react"

import { observer } from "mobx-react-lite"
import { type FormEvent, useState } from "react"
import { useAppContext } from "../../../../shared/functions/Context"
import { hideModalFromId } from "../../../../shared/functions/ModalShow"
import MODAL_NAMES from "../../../dialogs/ModalName"
import { type ITemplates, defaultTemplate } from "../../../../shared/models/three-sixty-feedback-models/Templates"

const CreateNewTemplateModal = observer(() => {
  const { api, store } = useAppContext()
  const me = store.auth.meJson
  const [template, setTemplate] = useState<ITemplates>({
    ...defaultTemplate,
    statements: [],
  })
  const [loading, setLoading] = useState(false)
  const [statements, setStatements] = useState<{ id: string; statement: string }[]>([
    { id: new Date().toISOString(), statement: "" },
    { id: new Date().toISOString(), statement: "" },
    { id: new Date().toISOString(), statement: "" },
  ])

  const handleStatementChange = (index: number, statement: string) => {
    const updatedStatements = [...statements]
    updatedStatements[index].statement = statement
    setStatements(updatedStatements)
  }

  const addStatement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const newStatement = {
      id: new Date().toISOString(),
      statement: "",
    }
    setStatements([...statements, newStatement])
  }

  const deleteStatement = (index: number) => {
    const updatedStatements = statements.filter((_, i) => i !== index)
    setStatements(updatedStatements)
  }

  const handleSaveTemplate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    // Ensure the statements are formatted correctly for the template
    const updatedTemplate: ITemplates = {
      ...template,
      statements: statements.map(({ id, statement }) => ({
        id,
        statement,
        rating: 0,
      })),
    }

    if (template.title.trim() !== "" && updatedTemplate.statements.length > 0) {
      await api.templates.create(updatedTemplate)
      setTemplate({ ...defaultTemplate, statements: [] })
      onCancel()
    } else {
      alert("Please add a title and at least one question to the template.")
    }

    setLoading(false)
  }

  const onCancel = () => {
    setTemplate({ ...defaultTemplate, statements: [] })
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_TEMPLATE_MODAL)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Create New Template</h3>
            </div>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors" type="button">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSaveTemplate} className="p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
              <input
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                type="text"
                placeholder="Enter template name"
                value={template.title}
                onChange={(e) =>
                  setTemplate({
                    ...template,
                    title: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Statements</label>
              <div className="space-y-3">
                {statements.map((statement, index) => (
                  <div key={statement.id} className="flex gap-2">
                    <input
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      type="text"
                      placeholder={`Enter Statement ${index + 1}`}
                      value={statement.statement}
                      onChange={(e) => handleStatementChange(index, e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => deleteStatement(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M5 20a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8h2V6h-4V4a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v2H3v2h2zM9 4h6v2H9zM8 8h9v12H7V8z"></path>
                        <path d="M9 10h2v8H9zm4 0h2v8h-2z"></path>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addStatement}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Statement
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Template"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
})

export default CreateNewTemplateModal
